/**
 * Where the kill camera's window sits and how big it is — the movable,
 * resizable half of AD-015 (user request, 2026-08-26: *"make the pip camera
 * window movable and closable by its title bar, resizable (persisted to local
 * storage without risk of being stored off screen on resolution changes)"*).
 *
 * `killcam.ts` keeps the *default* corner placement, which is what the window
 * has whenever the pilot has never moved it; this file is what happens once he
 * has. Pure, and free of both `three` and the DOM, for the same reason
 * `killcam.ts` is: three separate things draw this rectangle — the scene's
 * scissored pass (`scene-killcam.ts`), the DOM frame around it
 * (`KillCamFrame.tsx`) and the hole the painted cockpit punches through itself
 * (`CockpitPanel.tsx`) — and the only thing keeping them agreed is that all
 * three call `resolveKillCamRect` with the same placement.
 *
 * ## The aspect is locked to 4:3, on purpose
 *
 * A stored placement is three numbers, not four: the height always follows the
 * width through `KILL_CAM_LAYOUT.aspect`. Two reasons, and the second is not a
 * matter of taste:
 *
 * - The window is a picture-in-picture of the original's own screen shape, and
 *   the frame it is drawn in restates that shape (`app.css`).
 * - `renderKillCam` sets `camera.aspect = rect.width / rect.height`, so a free
 *   aspect is a **field of view** control wearing a resize handle's clothes.
 *   Dragged wide, the vertical field narrows and the fall — a wreck coming
 *   *down* — leaves the frame from the top and bottom, which is precisely the
 *   thing this window exists to show. One degree of freedom also makes the
 *   clamp below a single interval rather than a rectangle-fitting problem.
 *
 * ## The off-screen trap
 *
 * A placement stored on a 3840x2160 screen and restored in a 1280x720 window
 * would put the title bar — the only handle the thing has — somewhere the
 * pointer cannot reach, and the pilot would have no way back short of clearing
 * storage. So **every read clamps**, and the clamp puts the window *wholly*
 * inside the view rather than merely leaving a sliver of it visible. That is
 * stricter than it has to be for reachability and it is the renderer that asks
 * for it: `setScissor`/`setViewport` take a rectangle in the swapchain, and a
 * window hanging half off the edge would have its picture cut by the GPU while
 * its DOM frame carried on being drawn around the missing part.
 *
 * The stored value is **never rewritten by a resize**. Clamping on read means a
 * pilot who shrinks his browser and grows it again finds the window back where
 * he left it, instead of having had its position quietly destroyed by a
 * transient layout. Only a drag or a resize *gesture* commits.
 */

import type { SettingsStore } from "../audio/settings.ts";
import { KILL_CAM_LAYOUT, killCamRect, type KillCamLayout, type KillCamRect } from "./killcam.ts";

/**
 * A stored window layout: the top-left corner and the width, in **CSS pixels
 * measured from the top-left of the view** — the same units and origin
 * `KillCamRect` uses, so nothing has to be converted between the store, the
 * renderer and the stylesheet.
 */
export interface KillCamPlacement {
  readonly xPx: number;
  readonly yPx: number;
  readonly widthPx: number;
}

export const KILL_CAM_PLACEMENT_STORAGE_KEY = "red-opus/killcam-window";

/**
 * The largest share of the view a *placed* window may claim, in whichever
 * dimension binds first. Past this it has stopped being a corner window and
 * become a split screen, and the main view is the one the player is flying on
 * — the same judgement `killCamRect`'s half-height ceiling makes for the
 * default placement, restated as a clamp because a pilot dragging a handle
 * wants the handle to stop rather than the window to vanish.
 */
export const KILL_CAM_MAX_VIEW_FRACTION = 0.6;

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * The widest this view can hold, honouring both the fraction ceiling above and
 * the 4:3 lock (a short, wide view is limited by its height).
 */
export function maxKillCamWidth(
  viewWidthCss: number,
  viewHeightCss: number,
  layout: KillCamLayout = KILL_CAM_LAYOUT,
): number {
  return Math.min(viewWidthCss, viewHeightCss * layout.aspect) * KILL_CAM_MAX_VIEW_FRACTION;
}

/**
 * A stored placement as a rectangle this view can actually show, or `null` when
 * the view is too small to hold even a legible one (`minWidthPx`) — in which
 * case nothing is drawn, exactly as for a default placement in the same view.
 */
export function clampKillCamPlacement(
  placement: KillCamPlacement,
  viewWidthCss: number,
  viewHeightCss: number,
  layout: KillCamLayout = KILL_CAM_LAYOUT,
): KillCamRect | null {
  const maxWidth = maxKillCamWidth(viewWidthCss, viewHeightCss, layout);
  if (maxWidth < layout.minWidthPx) return null;
  const width = Math.round(clampNumber(placement.widthPx, layout.minWidthPx, maxWidth));
  const height = Math.round(width / layout.aspect);
  return {
    x: Math.round(clampNumber(placement.xPx, 0, viewWidthCss - width)),
    y: Math.round(clampNumber(placement.yPx, 0, viewHeightCss - height)),
    width,
    height,
  };
}

/**
 * The window's rectangle in a view of this size: the pilot's own placement if
 * he has made one, otherwise `killcam.ts`'s default corner — which keeps a
 * player who never touches the thing on exactly the geometry AD-015 shipped.
 */
export function resolveKillCamRect(
  placement: KillCamPlacement | null,
  viewWidthCss: number,
  viewHeightCss: number,
  layout: KillCamLayout = KILL_CAM_LAYOUT,
): KillCamRect | null {
  if (placement === null) return killCamRect(viewWidthCss, viewHeightCss, layout);
  return clampKillCamPlacement(placement, viewWidthCss, viewHeightCss, layout);
}

/** The three numbers worth storing out of a rectangle the view has already accepted. */
export function placementOfRect(rect: KillCamRect): KillCamPlacement {
  return { xPx: rect.x, yPx: rect.y, widthPx: rect.width };
}

/** A title-bar drag: the whole window travels with the pointer, size untouched. */
export function movedKillCamPlacement(origin: KillCamPlacement, dxPx: number, dyPx: number): KillCamPlacement {
  return { xPx: origin.xPx + dxPx, yPx: origin.yPx + dyPx, widthPx: origin.widthPx };
}

/**
 * A corner-handle drag, with the top-left corner pinned and the aspect locked.
 *
 * The handle has two axes and the window has one, so the travel is projected
 * onto the window's own diagonal: `dx` and the width-equivalent of `dy` are
 * averaged. Dragging straight out along either edge therefore still grows the
 * window (at half rate), and dragging along the diagonal — which is what the
 * `nwse-resize` cursor invites — tracks the pointer exactly.
 */
export function resizedKillCamPlacement(
  origin: KillCamPlacement,
  dxPx: number,
  dyPx: number,
  layout: KillCamLayout = KILL_CAM_LAYOUT,
): KillCamPlacement {
  return { xPx: origin.xPx, yPx: origin.yPx, widthPx: origin.widthPx + (dxPx + dyPx * layout.aspect) / 2 };
}

/**
 * Whatever survives a `JSON.parse` of untrusted stored text, as a placement or
 * as `null` — and `null` is not a failure here, it is "use the default corner",
 * which is also the honest answer for a corrupt or half-written record.
 */
export function normalizeKillCamPlacement(candidate: unknown): KillCamPlacement | null {
  if (typeof candidate !== "object" || candidate === null) return null;
  const raw = candidate as Partial<Record<keyof KillCamPlacement, unknown>>;
  const xPx = Number(raw.xPx);
  const yPx = Number(raw.yPx);
  const widthPx = Number(raw.widthPx);
  if (!Number.isFinite(xPx) || !Number.isFinite(yPx) || !Number.isFinite(widthPx)) return null;
  return { xPx, yPx, widthPx };
}

/** `null` on a missing key, corrupt JSON or a malformed payload — never throws. */
export function loadKillCamPlacement(store: SettingsStore): KillCamPlacement | null {
  const raw = store.getItem(KILL_CAM_PLACEMENT_STORAGE_KEY);
  if (raw === null) return null;
  try {
    return normalizeKillCamPlacement(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveKillCamPlacement(store: SettingsStore, placement: KillCamPlacement): void {
  store.setItem(KILL_CAM_PLACEMENT_STORAGE_KEY, JSON.stringify(placement));
}
